import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import { IoLockClosedOutline, IoConstructOutline, IoDocumentTextOutline, IoArrowUpOutline, IoImageOutline } from 'react-icons/io5'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import {
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea
} from '@mantine/core'
import { AmmoScoreBadge, EmptyState, PostCard, TagPicker } from '../../app/common/ui'
import type { RegistryCommentDto } from '../../app/models/registryComment'
import LoadingComponent from '../../app/layout/LoadingComponents'
import Breadcrumb from '../../app/common/Breadcrumb'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import type { RelatedModelDto } from '../../app/api/agent'
import { Post } from '../../app/models/post'
import { TagTargetType } from '../../app/models/tag'
import type { TagDto } from '../../app/models/tag'
import type { FirearmBuild } from '../../app/models/build'
import type { FirearmModelFormValues } from '../../app/models/firearmModel'
import MobileImageCapture from '../../app/common/imageUpload/MobileImageCapture'
import SerialBlurModal from '../../app/common/imageUpload/SerialBlurModal'
import { ensureUploadableImageFile } from '../../app/common/imageUpload/ensureUploadableImageFile'
import AddToVaultButton from '../gunvault/AddToVaultButton'
import AddToWishlistButton from '../wishlist/AddToWishlistButton'
import CommunityPhotosGallery from '../contentPhotos/CommunityPhotosGallery'
import BuildCardThumbnail from '../builds/BuildCardThumbnail'
import { canCreateCommunityPost, MIN_AMMO_SCORE_TO_CREATE_POST } from '../../app/common/constants/reputation'

/** Must match backend AmmoScoreCalculator. */
const MIN_AMMO_SCORE_TO_EDIT_TAGS = 50
const MIN_AMMO_SCORE_TO_PROPOSE_EDIT = 100

function registryCommentInitials (displayName?: string | null, username?: string) {
  if (displayName?.trim()) {
    const parts = displayName.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return displayName.slice(0, 2).toUpperCase()
  }
  if (username?.trim()) return username.trim().slice(0, 2).toUpperCase()
  return '?'
}

const STATUS_LABEL: Record<number, string> = {
  0: 'Pending approval',
  1: 'Approved',
  2: 'Rejected'
}

const editValidationSchema = Yup.object({
  make: Yup.string().required('Make is required').max(200, 'Max 200 characters'),
  model: Yup.string().required('Model is required').max(200, 'Max 200 characters'),
  platform: Yup.string().max(200),
  caliber: Yup.string().max(100),
  actionType: Yup.string().max(100),
  yearIntroduced: Yup.number().nullable().min(1800).max(2100),
  description: Yup.string().max(5000)
})

export default observer(function RegistryModelDetails () {
  const history = useHistory()
  const { firearmModelStore, userStore } = useStore()
  const {
    selectedModel,
    loadModel,
    loadingInitial,
    approveModel,
    rejectModel,
    deleteModel,
    clearSelectedModel,
    updateModel
  } = firearmModelStore
  const { id } = useParams<{ id: string }>()
  const location = useLocation<{ fromPost?: boolean; postId?: string; postTitle?: string }>()
  const fromPost = location.state?.fromPost
  const sourcePostId = location.state?.postId
  const sourcePostTitle = location.state?.postTitle

  const [reviews, setReviews] = useState<Post[]>([])
  const [ownershipTips, setOwnershipTips] = useState<Post[]>([])
  const [tags, setTags] = useState<TagDto[]>([])
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [builds, setBuilds] = useState<FirearmBuild[]>([])
  const [buildsLoaded, setBuildsLoaded] = useState(false)
  const [buildsLoading, setBuildsLoading] = useState(false)
  const [modelStats, setModelStats] = useState<{ vaultCount: number; buildCount: number; postCount: number; avgVoteScore: number } | null>(null)
  const [relatedModels, setRelatedModels] = useState<RelatedModelDto[]>([])
  const [adminEditOpen, setAdminEditOpen] = useState(false)
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [hideModalOpen, setHideModalOpen] = useState(false)
  const [hideReason, setHideReason] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [changingCover, setChangingCover] = useState(false)
  const [coverModalOpen, setCoverModalOpen] = useState(false)
  const [coverUrlSectionOpen, setCoverUrlSectionOpen] = useState(false)
  const [coverUrlDraft, setCoverUrlDraft] = useState('')
  const [pendingCoverImageFile, setPendingCoverImageFile] = useState<File | null>(null)
  const [registryComments, setRegistryComments] = useState<RegistryCommentDto[]>([])
  const [registryCommentBody, setRegistryCommentBody] = useState('')
  const [registryCommentsLoading, setRegistryCommentsLoading] = useState(false)
  const [submittingRegistryComment, setSubmittingRegistryComment] = useState(false)

  useEffect(() => {
    if (id) loadModel(id)
    return () => clearSelectedModel()
  }, [id, loadModel, clearSelectedModel])

  useEffect(() => {
    if (!id) return
    agent.Posts.listByFirearmModel(id, 1).then(setReviews).catch(() => setReviews([]))
    agent.Posts.listByFirearmModel(id, 2).then(setOwnershipTips).catch(() => setOwnershipTips([]))
  }, [id])

  useEffect(() => {
    if (!id) return
    setRegistryCommentsLoading(true)
    agent.FirearmModels.listRegistryComments(id)
      .then((c) => setRegistryComments(c ?? []))
      .catch(() => setRegistryComments([]))
      .finally(() => setRegistryCommentsLoading(false))
  }, [id])

  useEffect(() => {
    if (!id) return
    agent.FirearmModels.getStats(id).then(setModelStats).catch(() => setModelStats(null))
    agent.FirearmModels.getRelated(id, 6).then(setRelatedModels).catch(() => setRelatedModels([]))
  }, [id])

  useEffect(() => {
    if (selectedModel?.tags != null) setTags(selectedModel.tags ?? [])
  }, [selectedModel?.tags])

  const breadcrumbItems = useMemo(() => {
    const base = [
      { label: 'Registry', href: '/registry' },
      {
        label: selectedModel
          ? `${selectedModel.make} ${selectedModel.model}`.trim() || 'Model'
          : 'Model'
      }
    ]
    if (fromPost && sourcePostId) {
      return [
        {
          label: sourcePostTitle ? `"${sourcePostTitle}"` : 'Back to post',
          href: `/blog/${sourcePostId}`
        },
        ...base
      ]
    }
    return base
  }, [selectedModel?.make, selectedModel?.model, fromPost, sourcePostId, sourcePostTitle])

  const handleApprove = async () => {
    if (!id) return
    try {
      await approveModel(id)
    } catch {
      // toast handled globally
    }
  }

  const handleRejectConfirm = async () => {
    if (!id) return
    try {
      await rejectModel(id, rejectReason.trim() || undefined)
      setRejectModalOpen(false)
      setRejectReason('')
    } catch {
      // toast handled globally
    }
  }

  const handleHideConfirm = async () => {
    if (!id) return
    try {
      await agent.FirearmModels.hide(id, hideReason.trim() || undefined)
      setHideModalOpen(false)
      setHideReason('')
      await loadModel(id)
    } catch {
      // toast globally
    }
  }

  const handleAddRegistryComment = async () => {
    if (!id || !registryCommentBody.trim()) return
    setSubmittingRegistryComment(true)
    try {
      const c = await agent.FirearmModels.addRegistryComment(id, registryCommentBody.trim())
      setRegistryComments((prev) => [...prev, c])
      setRegistryCommentBody('')
    } catch {
      // toast globally
    } finally {
      setSubmittingRegistryComment(false)
    }
  }

  if (firearmModelStore.viewError && !selectedModel) {
    return (
      <Box>
        <Box mb="md">
          <Text
            component={Link}
            to={fromPost && sourcePostId ? `/blog/${sourcePostId}` : '/registry'}
            size="sm"
            sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
          >
            {fromPost && sourcePostId ? '← Back to post' : '← Back to Registry'}
          </Text>
        </Box>
        <Box sx={{ padding: '24px', textAlign: 'center', color: 'var(--gs-muted)' }}>
          {firearmModelStore.viewError}
          {firearmModelStore.viewError?.includes('log in') && (
            <Box mt="md">
              <Button component={Link} to="/blog" variant="light">
                Go to log in
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  if (loadingInitial || !selectedModel) {
    return <LoadingComponent content="Loading model..." />
  }

  const model = selectedModel

  const openCoverModal = () => {
    setCoverUrlDraft(model.coverImageUrl ?? '')
    setCoverUrlSectionOpen(false)
    setPendingCoverImageFile(null)
    setCoverModalOpen(true)
  }

  const handleCoverFileSelected = (file: File) => {
    setPendingCoverImageFile(file)
  }

  const doCoverUpload = async (file: File) => {
    const modelId = id
    if (!modelId) return
    let normalized: File
    try {
      normalized = await ensureUploadableImageFile(file)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not prepare image for upload.')
      return
    }
    try {
      setChangingCover(true)
      await firearmModelStore.uploadImage(modelId, normalized)
      toast.success('Cover image updated.')
      setCoverModalOpen(false)
      setPendingCoverImageFile(null)
    } catch {
      // toast via interceptor
    } finally {
      setChangingCover(false)
    }
  }

  const applyCoverUrl = async () => {
    const url = coverUrlDraft.trim()
    if (!id) return
    if (url && !/^https?:\/\//i.test(url)) {
      toast.error('URL must start with http:// or https://')
      return
    }
    try {
      setChangingCover(true)
      await updateModel(id, { coverImageUrl: url })
      toast.success(url ? 'Cover URL updated.' : 'Cover image cleared.')
      setCoverModalOpen(false)
    } catch {
      // toast via interceptor
    } finally {
      setChangingCover(false)
    }
  }

  const ammoScore = userStore.user?.ammoScore ?? 0
  const canEditRegistry =
    userStore.isLoggedIn &&
    (userStore.isAdmin ||
      userStore.isModerator ||
      (userStore.user?.username === model.submittedByUsername && model.status === 0) ||
      ammoScore >= MIN_AMMO_SCORE_TO_PROPOSE_EDIT)
  const canEditTags =
    userStore.isLoggedIn &&
    (userStore.isAdmin || userStore.isModerator || ammoScore >= MIN_AMMO_SCORE_TO_EDIT_TAGS)

  const isAdminOrMod = userStore.isAdmin || userStore.isModerator
  const canChangeCover = canEditRegistry

  return (
    <Box>
      <Box mb="md" sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text
          component={Link}
          to={fromPost && sourcePostId ? `/blog/${sourcePostId}` : '/registry'}
          size="sm"
          sx={{ color: 'var(--gs-muted)', textDecoration: 'none', '&:hover': { color: 'var(--gs-text)' } }}
        >
          {fromPost && sourcePostId ? '← Back to post' : '← Back to Registry'}
        </Text>
        <Breadcrumb items={breadcrumbItems} />
      </Box>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          height: 240,
          borderRadius: 12,
          overflow: 'hidden',
          marginBottom: 12,
          background: model.coverImageUrl
            ? undefined
            : 'linear-gradient(135deg, #0D1117, #1a0e05)'
        }}
      >
        {model.coverImageUrl
          ? (
          <img
            src={model.coverImageUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
            )
          : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#30363D'
            }}
          >
            <IoImageOutline size={48} />
          </Box>
            )}
        {canChangeCover && (
          <Button
            size="xs"
            sx={{
              position: 'absolute',
              top: 10,
              right: 12,
              background: 'rgba(13,17,23,0.75)',
              border: '1px solid var(--gs-border-bright)',
              color: 'var(--gs-text)',
              padding: '5px 10px',
              borderRadius: 5,
              fontSize: 11,
              fontWeight: 500,
              '&:hover': { background: 'rgba(22,27,34,0.9)' }
            }}
            onClick={openCoverModal}
          >
            Change Cover
          </Button>
        )}
      </Box>

      {/* Quick stats bar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          padding: '12px 24px',
          borderBottom: '1px solid var(--gs-border)',
          marginBottom: 16,
          background: 'var(--gs-surface)',
          borderRadius: 8
        }}
      >
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <Text size="xs" color="dimmed">
            Platform
          </Text>
          <Badge size="sm" radius="sm" mt={4}>
            {model.platform || '—'}
          </Badge>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <Text size="xs" color="dimmed">
            Caliber
          </Text>
          <Text size="sm" mt={4}>
            {model.caliber || '—'}
          </Text>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <Text size="xs" color="dimmed">
            Action Type
          </Text>
          <Text size="sm" mt={4}>
            {model.actionType || '—'}
          </Text>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <Text size="xs" color="dimmed">
            Year Introduced
          </Text>
          <Text size="sm" mt={4}>
            {model.yearIntroduced ?? '—'}
          </Text>
        </Box>
        <Box sx={{ padding: '8px 20px 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <Text size="xs" color="dimmed">
            Vote Score
          </Text>
          <Text size="sm" mt={4} sx={{ color: 'var(--gs-green)', fontWeight: 600 }}>
            ▲ {model.voteScore ?? 0}
          </Text>
        </Box>
        <Box sx={{ padding: '8px 0 8px 0', marginRight: 20, borderRight: '1px solid var(--gs-border)' }}>
          <Text size="xs" color="dimmed">
            Builds
          </Text>
          <Button
            variant="subtle"
            size="xs"
            mt={2}
            sx={{ padding: 0, height: 'auto', fontSize: 12 }}
            onClick={() => history.push(`/builds?firearmModelId=${model.id}`)}
          >
            {model.buildCount ?? 0} build{(model.buildCount ?? 0) === 1 ? '' : 's'}
          </Button>
        </Box>
        <Box sx={{ padding: '8px 0 8px 0', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <AddToVaultButton firearmModelId={model.id} />
          <AddToWishlistButton firearmModelId={model.id} />
        </Box>
      </Box>

      {/* Main layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 300px',
          gap: 16,
          alignItems: 'flex-start',
          '@media (max-width: 900px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
          }
        }}
      >
        {/* Main column */}
        <Box>
          <Box mb="sm">
            <Text
              sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 28,
                textTransform: 'uppercase',
                color: 'var(--gs-text)'
              }}
            >
              {model.make} {model.model}
            </Text>
            <Group spacing="xs" mt={4}>
              {model.submittedByUsername && (
                <Text size="xs" color="dimmed">
                  Submitted by{' '}
                  <Link to={`/profiles/${model.submittedByUsername}`}>
                    @{model.submittedByUsername}
                  </Link>
                </Text>
              )}
              <Text size="xs" color="dimmed">
                · {format(new Date(model.createdAt), 'MMM d, yyyy')}
              </Text>
              {model.approvedAt && model.status === 1 && (
                <Text size="xs" color="dimmed">
                  · Approved {format(new Date(model.approvedAt), 'MMM d, yyyy')}
                </Text>
              )}
            </Group>
            {model.isVerified && (
              <Box
                sx={{
                  display: 'inline-block',
                  marginTop: 8,
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 700,
                  background: 'rgba(63,185,80,0.15)',
                  border: '1px solid rgba(63,185,80,0.3)',
                  color: 'var(--gs-green)'
                }}
              >
                ✓ Community Verified
              </Box>
            )}
          </Box>

          {/* ── Social proof strip ── */}
          {modelStats && (
            <Box
              sx={{
                display: 'flex',
                gap: 0,
                marginBottom: 16,
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: 8,
                overflow: 'hidden'
              }}
            >
              {[
                {
                  icon: <IoLockClosedOutline size={14} />,
                  value: modelStats.vaultCount,
                  label: 'own this',
                  tab: null,
                  color: '#C9A84C'
                },
                {
                  icon: <IoConstructOutline size={14} />,
                  value: modelStats.buildCount,
                  label: 'builds',
                  tab: 'builds',
                  color: 'var(--gs-blue)'
                },
                {
                  icon: <IoDocumentTextOutline size={14} />,
                  value: modelStats.postCount,
                  label: 'posts',
                  tab: 'reviews',
                  color: 'var(--gs-green)'
                },
                {
                  icon: <IoArrowUpOutline size={14} />,
                  value: modelStats.avgVoteScore > 0 ? `+${modelStats.avgVoteScore.toFixed(1)}` : modelStats.avgVoteScore.toFixed(1),
                  label: 'avg score',
                  tab: null,
                  color: 'var(--gs-accent)'
                }
              ].map(({ icon, value, label, tab, color }, i) => (
                <Box
                  key={label}
                  component={tab ? 'button' : 'div'}
                  type={tab ? 'button' : undefined}
                  onClick={tab ? () => setActiveTab(tab) : undefined}
                  sx={{
                    flex: 1,
                    padding: '12px 8px',
                    textAlign: 'center',
                    borderRight: i < 3 ? '1px solid var(--gs-border)' : 'none',
                    background: 'none',
                    cursor: tab ? 'pointer' : 'default',
                    transition: 'background 0.15s',
                    '&:hover': tab ? { background: 'rgba(232,93,4,0.06)' } : {}
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 2, color }}>
                    {icon}
                    <Text component="span" sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 20, color, lineHeight: 1.1 }}>
                      {value}
                    </Text>
                  </Box>
                  <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: 'var(--gs-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                  </Text>
                </Box>
              ))}
            </Box>
          )}

          <Tabs value={activeTab} onTabChange={(v) => setActiveTab(v ?? 'overview')}>
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="reviews">Reviews</Tabs.Tab>
              <Tabs.Tab value="ownership">Ownership Tips</Tabs.Tab>
              <Tabs.Tab
                value="builds"
                onClick={async () => {
                  if (!id || buildsLoaded) return
                  setBuildsLoading(true)
                  try {
                    const result = await agent.Builds.list({
                      firearmModelId: id,
                      pageNumber: 1,
                      pageSize: 20
                    })
                    setBuilds(result.items ?? [])
                    setBuildsLoaded(true)
                  } catch {
                    setBuilds([])
                  } finally {
                    setBuildsLoading(false)
                  }
                }}
              >
                Builds
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="overview" pt="md">
              {model.description
                ? (
                <Text
                  sx={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: 15,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    marginBottom: 16
                  }}
                >
                  {model.description}
                </Text>
                  )
                : (
                <Box
                  sx={{
                    padding: '16px 20px',
                    marginBottom: 16,
                    background: 'var(--gs-surface2)',
                    border: '1px dashed var(--gs-border)',
                    borderRadius: 6
                  }}
                >
                  <Text sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-muted)', fontStyle: 'italic' }}>
                    No description yet for the {model.make} {model.model}.
                    {ammoScore >= MIN_AMMO_SCORE_TO_PROPOSE_EDIT
                      ? (
                      <>
                        {' '}
                        <Text
                          component="span"
                          sx={{ color: 'var(--gs-accent)', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => setAdminEditOpen(true)}
                        >
                          Propose an edit →
                        </Text>
                      </>
                        )
                      : (
                          ' Know this firearm? Reach Expert rank to propose an edit.'
                        )}
                  </Text>
                </Box>
                  )}
              {id && (
                <Box mt="md" mb="md">
                  <TagPicker
                    targetType={TagTargetType.FirearmModel}
                    targetId={id}
                    currentTags={tags}
                    onTagApplied={(tag: TagDto) => setTags((prev) => [...prev, tag])}
                    onTagRemoved={(tag: TagDto) =>
                      setTags((prev) => prev.filter((t) => t.id !== tag.id))
                    }
                    readOnly={!canEditTags}
                    aiContext={selectedModel ? { name: `${selectedModel.make} ${selectedModel.model}`.trim(), description: selectedModel.description ?? undefined } : undefined}
                  />
                </Box>
              )}

              {/* Chambers section */}
              {selectedModel?.compatibleCartridges && (selectedModel.compatibleCartridges.official.length > 0 || selectedModel.compatibleCartridges.alternate.length > 0) ? (
                <Box mt="xl" pt="lg" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                  <Group position="apart" mb="md">
                    <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16 }}>
                      Chambers
                    </Text>
                    {userStore.user?.isAdmin && (
                      <Button size="sm" variant="default" onClick={() => {
                        // TODO: Open add compatibility modal
                      }}>
                        + Add Caliber
                      </Button>
                    )}
                  </Group>

                  {selectedModel.compatibleCartridges.official.length > 0 && (
                    <Group spacing="xs" mb="md">
                      {selectedModel.compatibleCartridges.official.map((spec) => (
                        <Badge
                          key={spec.id}
                          component="a"
                          href={`/ammo/specs/${spec.id}`}
                          onClick={(e) => {
                            e.preventDefault()
                            history.push(`/ammo/specs/${spec.id}`)
                          }}
                          sx={{
                            cursor: 'pointer',
                            background: 'var(--gs-gold)',
                            color: '#000',
                            fontSize: 14,
                            padding: '8px 12px',
                            '&:hover': { opacity: 0.8 }
                          }}
                        >
                          {spec.name}
                        </Badge>
                      ))}
                    </Group>
                  )}

                  {selectedModel.compatibleCartridges.alternate.length > 0 && (
                    <Box>
                      <Text size="sm" sx={{ color: 'var(--gs-muted)', marginBottom: '8px' }}>
                        Also compatible:
                      </Text>
                      <Group spacing="xs">
                        {selectedModel.compatibleCartridges.alternate.map((spec) => (
                          <Badge
                            key={spec.id}
                            component="a"
                            href={`/ammo/specs/${spec.id}`}
                            onClick={(e) => {
                              e.preventDefault()
                              history.push(`/ammo/specs/${spec.id}`)
                            }}
                            sx={{
                              cursor: 'pointer',
                              background: 'var(--gs-surface2)',
                              color: 'var(--gs-muted)',
                              fontSize: 12,
                              padding: '6px 10px',
                              '&:hover': { color: 'var(--gs-text)' }
                            }}
                            variant="outline"
                          >
                            {spec.name}
                          </Badge>
                        ))}
                      </Group>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box mt="xl" pt="lg" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                  <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
                    Chambers
                  </Text>
                  <Text size="sm" sx={{ color: 'var(--gs-muted)', fontStyle: 'italic' }}>
                    Compatibility data not yet added.
                  </Text>
                </Box>
              )}

              {id && <CommunityPhotosGallery entityType="FirearmModel" entityId={id} />}
              <Box mt="xl" pt="lg" sx={{ borderTop: '1px solid var(--gs-border)' }}>
                <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, marginBottom: 12 }}>
                  Model discussion
                </Text>
                <Text size="sm" color="dimmed" mb="md" sx={{ fontFamily: 'DM Sans, sans-serif' }}>
                  Short notes and questions about this entry. For long-form content, use reviews or ownership tips.
                </Text>
                {userStore.isLoggedIn
                  ? (
                  <>
                    <Textarea
                      placeholder="Add a note about this model…"
                      value={registryCommentBody}
                      onChange={(e) => setRegistryCommentBody(e.currentTarget.value)}
                      minRows={2}
                      mb="sm"
                      sx={{ fontFamily: 'DM Sans, sans-serif' }}
                    />
                    <Button
                      size="sm"
                      color="ember"
                      onClick={() => void handleAddRegistryComment()}
                      loading={submittingRegistryComment}
                      disabled={!registryCommentBody.trim()}
                    >
                      Post
                    </Button>
                  </>
                    )
                  : (
                  <Text size="sm" color="dimmed" mb="md">
                    <Text component={Link} to="/login" sx={{ color: 'var(--gs-accent)' }}>
                      Sign in
                    </Text>{' '}
                    to join the discussion.
                  </Text>
                    )}
                <Box mt="md">
                  {registryCommentsLoading
                    ? (
                    <Text size="sm" color="dimmed">
                      Loading comments…
                    </Text>
                      )
                    : registryComments.length === 0
                      ? (
                    <Text size="sm" color="dimmed" sx={{ fontStyle: 'italic' }}>
                      No comments yet.
                    </Text>
                        )
                      : (
                          registryComments.map((c) => (
                      <Group key={c.id} align="flex-start" spacing="sm" mb="md" noWrap>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: c.authorImage ? `url(${c.authorImage}) center/cover` : 'var(--gs-surface2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0
                          }}
                        >
                          {!c.authorImage && registryCommentInitials(c.authorDisplayName, c.authorUsername)}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Group spacing="xs">
                            <Text component={Link} to={`/profiles/${c.authorUsername}`} size="sm" weight={500}>
                              {c.authorDisplayName || c.authorUsername}
                            </Text>
                            <AmmoScoreBadge tier={c.authorAmmoTier ?? 'Newcomer'} score={c.authorAmmoScore ?? 0} size="sm" />
                            <Text size="xs" color="dimmed">
                              {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                            </Text>
                          </Group>
                          <Text size="sm" sx={{ marginTop: 4, fontFamily: 'DM Sans, sans-serif' }}>
                            {c.body}
                          </Text>
                        </Box>
                      </Group>
                          ))
                        )}
                </Box>
              </Box>
            </Tabs.Panel>

            <Tabs.Panel value="reviews" pt="md">
              {reviews.length === 0
                ? (
                <EmptyState
                  title="No reviews yet"
                  message="Be the first to review this model."
                  action={{
                    label: 'Write a Review',
                    onClick: () => {
                      if (!userStore.isLoggedIn) {
                        history.push('/login')
                        return
                      }
                      if (!canCreateCommunityPost(userStore.user)) {
                        toast.info(
                          `Reach ${MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore to publish reviews and other posts. Vote and comment to earn score.`,
                          { autoClose: 6000 }
                        )
                        return
                      }
                      history.push(`/createPost?firearmModelId=${model.id}&type=1`)
                    }
                  }}
                />
                  )
                : (
                <Box>
                  {reviews.map((post) => (
                    <Box key={post.id} mb="sm">
                      <PostCard
                        id={post.id}
                        title={post.title}
                        excerpt={post.excerpt}
                        type={post.type}
                        coverImageUrl={post.coverImageUrl}
                        mainPhotoUrl={post.mainPhotoUrl}
                        authorUsername={post.authorUsername}
                        authorDisplayName={post.authorDisplayName}
                        authorImage={post.authorImage}
                        authorTier={post.authorAmmoTier}
                        authorScore={post.authorAmmoScore}
                        voteScore={post.voteScore}
                        currentUserVote={post.currentUserVote}
                        commentCount={post.comments?.length ?? 0}
                        createdAt={post.createdAt}
                        variant="compact"
                      />
                    </Box>
                  ))}
                </Box>
                  )}
            </Tabs.Panel>

            <Tabs.Panel value="ownership" pt="md">
              {ownershipTips.length === 0
                ? (
                <EmptyState
                  title="No ownership tips yet"
                  message="Share what it's like to own this model."
                  action={{
                    label: 'Share an Ownership Tip',
                    onClick: () => {
                      if (!userStore.isLoggedIn) {
                        history.push('/login')
                        return
                      }
                      if (!canCreateCommunityPost(userStore.user)) {
                        toast.info(
                          `Reach ${MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore to publish tips and other posts. Vote and comment to earn score.`,
                          { autoClose: 6000 }
                        )
                        return
                      }
                      history.push(`/createPost?firearmModelId=${model.id}&type=2`)
                    }
                  }}
                />
                  )
                : (
                <Box>
                  {ownershipTips.map((post) => (
                    <Box key={post.id} mb="sm">
                      <PostCard
                        id={post.id}
                        title={post.title}
                        excerpt={post.excerpt}
                        type={post.type}
                        coverImageUrl={post.coverImageUrl}
                        mainPhotoUrl={post.mainPhotoUrl}
                        authorUsername={post.authorUsername}
                        authorDisplayName={post.authorDisplayName}
                        authorImage={post.authorImage}
                        authorTier={post.authorAmmoTier}
                        authorScore={post.authorAmmoScore}
                        voteScore={post.voteScore}
                        currentUserVote={post.currentUserVote}
                        commentCount={post.comments?.length ?? 0}
                        createdAt={post.createdAt}
                        variant="compact"
                      />
                    </Box>
                  ))}
                </Box>
                  )}
            </Tabs.Panel>

            <Tabs.Panel value="builds" pt="md">
              {buildsLoading
                ? (
                <LoadingComponent content="Loading builds..." />
                  )
                : builds.length === 0
                  ? (
                <EmptyState
                  title="No builds yet"
                  message="Create a build that uses this model."
                  action={{
                    label: 'Create a Build',
                    onClick: () =>
                      history.push(`/builds/new?firearmModelId=${model.id}`)
                  }}
                />
                    )
                  : (
                <Box>
                  {builds.map((b) => (
                    <Card
                      key={b.id}
                      withBorder
                      padding="sm"
                      mb="xs"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => history.push(`/builds/${b.id}`)}
                    >
                      <Group align="flex-start" spacing="sm" noWrap>
                        <BuildCardThumbnail url={b.mainPhotoUrl} alt={b.name} variant="compact" />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Text weight={600} size="sm">
                            {b.name}
                          </Text>
                          <Text size="xs" color="dimmed">
                            by {b.ownerUsername} · {b.parts?.length ?? 0} parts · ▲{' '}
                            {b.voteScore ?? 0}
                          </Text>
                        </Box>
                      </Group>
                    </Card>
                  ))}
                </Box>
                    )}
            </Tabs.Panel>

          </Tabs>
        </Box>

        {/* Sidebar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Community stats */}
          <Card withBorder padding="md">
            <Text
              size="xs"
              color="dimmed"
              sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}
              mb={8}
            >
              Community
            </Text>
            {modelStats
              ? (
              <Box>
                {[
                  ['Vault owners', modelStats.vaultCount],
                  ['Builds', modelStats.buildCount],
                  ['Posts & reviews', modelStats.postCount],
                  ['Avg vote score', modelStats.avgVoteScore]
                ].map(([label, value]) => (
                  <Group key={label as string} position="apart" spacing="xs" mb={4}>
                    <Text size="xs" color="dimmed">{label}</Text>
                    <Text size="xs" weight={600} sx={{ color: 'var(--gs-accent)' }}>{value}</Text>
                  </Group>
                ))}
              </Box>
                )
              : (
              <Box>
                {[1, 2, 3, 4].map((n) => (
                  <Group key={n} position="apart" mb={4}>
                    <Skeleton height={10} width="45%" />
                    <Skeleton height={10} width="20%" />
                  </Group>
                ))}
              </Box>
                )}
          </Card>

          <Card withBorder padding="md">
            <Text
              size="xs"
              color="dimmed"
              sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}
              mb={6}
            >
              Quick specs
            </Text>
            <Box>
              {[
                ['Make', model.make],
                ['Model', model.model],
                ['Platform', model.platform || '—'],
                ['Caliber', model.caliber || '—'],
                ['Action Type', model.actionType || '—'],
                ['Year Introduced', model.yearIntroduced ?? '—']
              ].map(([label, value]) => (
                <Group key={label as string} position="apart" spacing="xs" mb={4}>
                  <Text size="xs" color="dimmed">
                    {label}
                  </Text>
                  <Text size="xs" weight={600}>
                    {value as string | number}
                  </Text>
                </Group>
              ))}
            </Box>
          </Card>

          <Card withBorder padding="md">
            <Text
              size="xs"
              color="dimmed"
              sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}
              mb={6}
            >
              Submission info
            </Text>
            <Box>
              {model.submittedByUsername && (
                <Group position="apart" spacing="xs" mb={4}>
                  <Text size="xs" color="dimmed">
                    Submitted by
                  </Text>
                  <Link to={`/profiles/${model.submittedByUsername}`}>
                    <Text size="xs" weight={600}>
                      @{model.submittedByUsername}
                    </Text>
                  </Link>
                </Group>
              )}
              <Group position="apart" spacing="xs" mb={4}>
                <Text size="xs" color="dimmed">
                  Submitted on
                </Text>
                <Text size="xs" weight={600}>
                  {format(new Date(model.createdAt), 'MMM d, yyyy')}
                </Text>
              </Group>
              <Group position="apart" spacing="xs" mb={4}>
                <Text size="xs" color="dimmed">
                  Status
                </Text>
                <Badge size="xs" color={model.status === 1 ? 'green' : model.status === 0 ? 'yellow' : 'red'}>
                  {STATUS_LABEL[model.status] ?? 'Unknown'}
                </Badge>
              </Group>
              {model.approvedAt && (
                <Group position="apart" spacing="xs" mb={4}>
                  <Text size="xs" color="dimmed">
                    Approved on
                  </Text>
                  <Text size="xs" weight={600}>
                    {format(new Date(model.approvedAt), 'MMM d, yyyy')}
                  </Text>
                </Group>
              )}
            </Box>
          </Card>

          {isAdminOrMod && (
            <Card withBorder padding="md">
              <Text
                size="xs"
                color="dimmed"
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8
                }}
              >
                ADMIN TOOLS
              </Text>
              {model.status === 0 && (
                <>
                  <Button
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-green)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={handleApprove}
                  >
                    Approve Model
                  </Button>
                  <Button
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-red)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setRejectModalOpen(true)}
                  >
                    Reject Submission
                  </Button>
                  <Button
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-red)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Delete Submission
                  </Button>
                </>
              )}
              {model.status === 2 && userStore.isAdmin && (
                <Button
                  sx={{
                    background: 'var(--gs-surface2)',
                    border: '1.5px solid var(--gs-border-bright)',
                    color: 'var(--gs-red)',
                    width: '100%',
                    padding: 8,
                    borderRadius: 5,
                    fontSize: 12,
                    textAlign: 'left',
                    marginBottom: 6
                  }}
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  Delete Submission
                </Button>
              )}
              {model.status === 1 && (
                <>
                  <Button
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-text)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setAdminEditOpen((v) => !v)}
                  >
                    Edit Model Details
                  </Button>
                  <Button
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-red)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={() => setHideModalOpen(true)}
                  >
                    Hide Model
                  </Button>
                  <Button
                    sx={{
                      background: 'var(--gs-surface2)',
                      border: '1.5px solid var(--gs-border-bright)',
                      color: 'var(--gs-text)',
                      width: '100%',
                      padding: 8,
                      borderRadius: 5,
                      fontSize: 12,
                      textAlign: 'left',
                      marginBottom: 6
                    }}
                    onClick={async () => {
                      if (!id) return
                      try {
                        if (model.isVerified) await agent.FirearmModels.unverify(id)
                        else await agent.FirearmModels.verify(id)
                        await loadModel(id)
                      } catch {
                        // toast via interceptor
                      }
                    }}
                  >
                    {model.isVerified ? 'Remove Verification' : 'Mark as Verified'}
                  </Button>
                </>
              )}
            </Card>
          )}
        </Box>
      </Box>

      {/* Related Models */}
      {relatedModels.length > 0 && (
        <Box mt="xl">
          <Text
            size="xs"
            color="dimmed"
            sx={{ fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600, marginBottom: 12 }}
          >
            Related Models
          </Text>
          <SimpleGrid
            cols={2}
            breakpoints={[{ minWidth: 640, cols: 3 }, { minWidth: 960, cols: 6 }]}
            spacing="sm"
          >
            {relatedModels.map((r) => (
              <Card
                key={r.id}
                component={Link}
                to={`/registry/${r.id}`}
                withBorder
                padding="sm"
                radius="md"
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  '&:hover': {
                    borderColor: 'var(--gs-accent)',
                    boxShadow: '0 4px 16px rgba(232,93,4,.15)'
                  }
                }}
              >
                {r.coverImageUrl
                  ? (
                  <Card.Section mb="xs">
                    <img
                      src={r.coverImageUrl}
                      alt=""
                      style={{ width: '100%', height: 72, objectFit: 'cover' }}
                    />
                  </Card.Section>
                    )
                  : (
                  <Box
                    sx={{
                      height: 72,
                      marginBottom: 8,
                      background: 'var(--gs-surface2)',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#30363D'
                    }}
                  >
                    <IoImageOutline size={24} />
                  </Box>
                    )}
                <Text size="xs" weight={600} lineClamp={1}>
                  {r.make} {r.model}
                </Text>
                {r.caliber && (
                  <Text size="xs" color="dimmed" lineClamp={1}>{r.caliber}</Text>
                )}
                {r.isVerified && (
                  <Text size="xs" sx={{ color: 'var(--gs-green)' }}>✓</Text>
                )}
              </Card>
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Admin edit modal */}
      <Modal
        opened={adminEditOpen}
        onClose={() => setAdminEditOpen(false)}
        title="Edit model details"
        size="lg"
      >
        {model && (
          <Formik<FirearmModelFormValues>
            initialValues={{
              make: model.make ?? '',
              model: model.model ?? '',
              platform: model.platform ?? '',
              caliber: model.caliber ?? '',
              actionType: model.actionType ?? '',
              yearIntroduced: model.yearIntroduced ?? null,
              description: model.description ?? '',
              coverImageUrl: model.coverImageUrl ?? ''
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await updateModel(model.id, values)
                toast.success('Model updated')
                setAdminEditOpen(false)
                await loadModel(model.id)
              } catch {
                // toast via interceptor
              } finally {
                setSubmitting(false)
              }
            }}
          >
            {({ values, handleChange, isSubmitting }) => (
              <Form>
                <Stack spacing="sm">
                  <TextInput label="Make" name="make" value={values.make} onChange={handleChange} required />
                  <TextInput label="Model" name="model" value={values.model} onChange={handleChange} required />
                  <TextInput label="Platform" name="platform" value={values.platform ?? ''} onChange={handleChange} />
                  <TextInput label="Caliber" name="caliber" value={values.caliber ?? ''} onChange={handleChange} />
                  <TextInput label="Action type" name="actionType" value={values.actionType ?? ''} onChange={handleChange} />
                  <TextInput
                    label="Year introduced"
                    name="yearIntroduced"
                    type="number"
                    value={values.yearIntroduced ?? ''}
                    onChange={handleChange}
                  />
                  <Textarea label="Description" name="description" value={values.description ?? ''} onChange={handleChange} minRows={3} />
                  <TextInput label="Cover image URL" name="coverImageUrl" value={values.coverImageUrl ?? ''} onChange={handleChange} />
                  <Group position="right" mt="sm">
                    <Button variant="default" onClick={() => setAdminEditOpen(false)}>Cancel</Button>
                    <Button type="submit" loading={isSubmitting}>Save</Button>
                  </Group>
                </Stack>
              </Form>
            )}
          </Formik>
        )}
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete submission"
      >
        <Text size="sm" mb="md">
          Permanently delete &ldquo;{model?.make} {model?.model}&rdquo;? This cannot be undone.
        </Text>
        <Group position="right" spacing="xs">
          <Button variant="default" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={async () => {
              if (!id) return
              try {
                await deleteModel(id)
                setDeleteConfirmOpen(false)
                history.push('/registry')
              } catch {
                // toast via interceptor
              }
            }}
          >
            Delete
          </Button>
        </Group>
      </Modal>

      {/* Reject modal */}
      <Modal
        opened={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject submission"
      >
        <Text size="sm" mb="xs">
          Add an optional reason for rejection:
        </Text>
        <Textarea
          minRows={3}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.currentTarget.value)}
        />
        <Group position="right" mt="md" spacing="xs">
          <Button variant="default" onClick={() => setRejectModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleRejectConfirm}>
            Reject
          </Button>
        </Group>
      </Modal>

      <Modal
        opened={coverModalOpen}
        onClose={() => {
          setPendingCoverImageFile(null)
          setCoverModalOpen(false)
        }}
        title="Change cover image"
      >
        <Text size="xs" color="dimmed" mb="sm">
          Use a photo you own or have rights to. Registry content is subject to moderation.
        </Text>
        <Group align="flex-start" spacing="md" grow>
          <Paper
            component="button"
            type="button"
            onClick={() => setCoverUrlSectionOpen((v) => !v)}
            sx={{
              cursor: 'pointer',
              border: '1px solid var(--gs-border)',
              borderRadius: 12,
              width: 132,
              height: 132,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 8,
              background: 'var(--gs-surface)',
              color: 'var(--gs-muted)'
            }}
          >
            <Text size="xs" weight={500}>
              {coverUrlSectionOpen ? 'Hide URL field' : 'Set image URL'}
            </Text>
          </Paper>
          <MobileImageCapture
            onFile={handleCoverFileSelected}
            accept="image/jpeg,image/png,image/webp"
            loading={changingCover}
            disabled={changingCover}
            label="Upload image"
            variant="light"
            fullWidth
          />
        </Group>
        <Collapse in={coverUrlSectionOpen}>
          <Stack spacing="sm" mt="md">
            <TextInput
              label="Image URL"
              description="Direct https link. Leave empty and apply to remove the cover."
              placeholder="https://..."
              value={coverUrlDraft}
              onChange={(e) => setCoverUrlDraft(e.currentTarget.value)}
            />
            <Group position="right" spacing="xs">
              <Button variant="default" onClick={() => setCoverUrlSectionOpen(false)}>
                Close URL field
              </Button>
              <Button onClick={applyCoverUrl} loading={changingCover}>
                Apply URL
              </Button>
            </Group>
          </Stack>
        </Collapse>
      </Modal>

      <SerialBlurModal
        file={pendingCoverImageFile}
        onConfirm={doCoverUpload}
        onSkip={doCoverUpload}
        onClose={() => setPendingCoverImageFile(null)}
      />

      {/* Hide modal */}
      <Modal
        opened={hideModalOpen}
        onClose={() => setHideModalOpen(false)}
        title="Hide model"
      >
        <Text size="sm" mb="xs">
          Add an optional reason for hiding:
        </Text>
        <Textarea
          minRows={3}
          value={hideReason}
          onChange={(e) => setHideReason(e.currentTarget.value)}
        />
        <Group position="right" mt="md" spacing="xs">
          <Button variant="default" onClick={() => setHideModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleHideConfirm}>
            Hide
          </Button>
        </Group>
      </Modal>
    </Box>
  )
})
